import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Factory, Users, CalendarClock, Receipt, CheckCircle2, Loader2, MessageSquarePlus,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";

/** Each tile is "what the user picks", mapped to the backend slug.
 *  This is shared by both the individual flow and the manager flow. */
const LEDGER_OPTIONS = [
    { key: "factory",  icon: Factory,      titleAr: "دفتر المصانع",   titleEn: "Factories",        descAr: "متابعة حسابات المصانع: بيع وشراء وأجور صياغة",                    descEn: "Track factory accounts: sales, purchases, smithing fees" },
    { key: "client",   icon: Users,        titleAr: "دفتر العملاء",    titleEn: "Customers",        descAr: "حسابات العملاء — مبيعات، مدفوعات، رصيد متبقي",                    descEn: "Customer accounts — sales, payments, balances" },
    { key: "daily",    icon: CalendarClock,titleAr: "دفتر الحركة",     titleEn: "Daily Ledger",     descAr: "حركة الصندوق اليومية: مبيعات/مشتريات/مصاريف",                     descEn: "Daily cash-box movement: sales / purchases / expenses" },
    { key: "vouchers", icon: Receipt,      titleAr: "سندات الصرف",     titleEn: "Payment Vouchers", descAr: "سندات صرف للعملاء والمحل بترقيم تلقائي وقابلية الطباعة",          descEn: "Customer + shop payment vouchers, auto-numbered and printable" },
];

export default function LedgerSelection() {
    const navigate = useNavigate();
    const { company } = useAuth();
    const { lang } = useLang();
    const isManager = company?.account_type === "manager";
    // Every ledger (including daily) is optional — at least one is required.
    const [picked, setPicked] = useState(() => new Set());
    const [saving, setSaving] = useState(false);

    const toggle = (key) => {
        setPicked((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const selectAll = () => {
        setPicked(new Set(LEDGER_OPTIONS.map((o) => o.key)));
    };

    const count = picked.size;
    const maxOptions = LEDGER_OPTIONS.length;
    const canConfirm = count >= 1 && count <= maxOptions;

    const onConfirm = async () => {
        if (!canConfirm) {
            toast.error(lang === "ar" ? "اختر دفتراً واحداً على الأقل" : "Select at least one ledger");
            return;
        }
        setSaving(true);
        try {
            const ledgers = LEDGER_OPTIONS.map((o) => o.key).filter((k) => picked.has(k));
            await api.post("/auth/set-enabled-ledgers", { ledgers });
            const wantsDaily = picked.has("daily");
            // Managers: daily is for their employees; set "advanced" so they skip the picker.
            if (isManager && wantsDaily) {
                try {
                    await api.post("/auth/set-daily-variant", { variant: "advanced" });
                } catch { /* ignore — variant picker will catch it if needed */ }
            }
            let dest = "/";
            if (isManager) {
                dest = "/manager/employee-perms";
            } else if (wantsDaily) {
                dest = "/daily/choose";
            }
            window.location.assign(dest);
        } catch (err) {
            toast.error(formatApiError(err));
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen app-bg" dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
                <div className="mb-8 text-center">
                    <div className="inline-block rounded-full bg-amber-100 px-4 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-900">
                        {lang === "ar" ? "خطوة 1 من 2" : "Step 1 of 2"}
                    </div>
                    <h1 className="mt-4 font-heading text-3xl font-bold text-neutral-900 sm:text-4xl">
                        {lang === "ar" ? "اختر دفاترك" : "Choose your ledgers"}
                    </h1>
                    <p className="mx-auto mt-3 max-w-xl text-sm text-neutral-600 sm:text-base">
                        {lang === "ar"
                            ? "اختر الدفاتر التي تحتاجها لإدارة عملك (دفتر واحد على الأقل). كل الدفاتر اختيارية بما فيها دفتر الحركة."
                            : "Pick the ledgers you need (at least one). Every ledger — including the daily ledger — is optional."}
                    </p>
                </div>

                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                    <div
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            canConfirm
                                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                        }`}
                        data-testid="ls-count-badge"
                    >
                        {lang === "ar" ? `المحدد: ${count} / ${maxOptions} (أدنى 1)` : `Selected: ${count} / ${maxOptions} (min 1)`}
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={selectAll}
                        className="h-9"
                        data-testid="ls-select-all-btn"
                    >
                        {lang === "ar" ? "تحديد الكل" : "Select All"}
                    </Button>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {LEDGER_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        const selected = picked.has(opt.key);
                        return (
                            <button
                                key={opt.key}
                                type="button"
                                onClick={() => toggle(opt.key)}
                                aria-pressed={selected}
                                data-testid={`ls-tile-${opt.key}`}
                                className={`group relative rounded-2xl border-2 p-5 text-${lang === "ar" ? "right" : "left"} transition ${
                                    selected
                                        ? "border-amber-700 bg-amber-50/70 shadow-md"
                                        : "border-neutral-200 bg-white hover:border-amber-300 hover:shadow-md"
                                }`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className={`rounded-xl p-2 ${selected ? "bg-amber-700 text-white" : "bg-neutral-100 text-neutral-600"}`}>
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    {selected ? (
                                        <CheckCircle2 className="h-6 w-6 text-amber-700" />
                                    ) : (
                                        <div className="h-6 w-6 rounded-full border-2 border-neutral-300" />
                                    )}
                                </div>
                                <h3 className="mt-4 font-heading text-lg font-bold text-neutral-900">
                                    {lang === "ar" ? opt.titleAr : opt.titleEn}
                                </h3>
                                <p className="mt-1 text-sm leading-relaxed text-neutral-600">
                                    {lang === "ar" ? opt.descAr : opt.descEn}
                                </p>
                            </button>
                        );
                    })}
                    <button
                        type="button"
                        onClick={() => navigate("/support?from=select-ledgers")}
                        data-testid="ls-request-ledger-tile"
                        className={`group relative rounded-2xl border-2 border-dashed border-amber-300 bg-white p-5 text-${lang === "ar" ? "right" : "left"} transition hover:border-amber-600 hover:bg-amber-50/40 hover:shadow-md`}
                    >
                        <div className="w-fit rounded-xl bg-amber-100 p-2 text-amber-800"><MessageSquarePlus className="h-5 w-5" /></div>
                        <h3 className="mt-4 font-heading text-lg font-bold text-neutral-900">{lang === "ar" ? "طلب دفتر" : "Request a ledger"}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-neutral-600">
                            {lang === "ar"
                                ? "تحتاج دفتراً غير موجود أو تعديلاً خاصاً؟ اشرح طلبك لإدارة معيار في المحادثة."
                                : "Need a ledger that isn't listed or a custom change? Describe it to Meyar admin in chat."}
                        </p>
                    </button>
                </div>

                <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-end">
                    <Button
                        type="button"
                        size="lg"
                        onClick={onConfirm}
                        disabled={!canConfirm || saving}
                        data-testid="ls-confirm-btn"
                        className="h-11 gap-2 bg-amber-700 px-6 text-white hover:bg-amber-800"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {lang === "ar" ? "تأكيد" : "Confirm"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
